import { LAST_SEO_TAB, recall } from "../../session";

/** The search and AI page's four tabs, on their own and small, because the
 *  shell drawn at launch (seo/Shell.tsx) has to light the right one before the
 *  page that owns them has downloaded, and should not pull the page in with it.
 *
 *  Four because the page answers four different questions, read at different
 *  times: how are we doing (Overview), what do the assistants say (AI answers),
 *  what do Google and Bing do (Search), and what is asked and what it costs
 *  (Settings). One long page made the first of them a scroll past the others.
 *
 *  Labels short enough that all four sit across a 320px phone without the row
 *  scrolling: a tab that is off the edge reads as a page with three tabs, and
 *  the unsaved-changes dot on Settings would be the part cut off. */
export type SeoTab = "overview" | "ai" | "search" | "settings";

export const SEO_TABS: { key: SeoTab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "ai", label: "AI" },
  { key: "search", label: "Search" },
  { key: "settings", label: "Settings" },
];

export function isSeoTab(value: unknown): value is SeoTab {
  return SEO_TABS.some((t) => t.key === value);
}

/** The tab to open on: the URL's, then the one last open this visit, then the
 *  overview. The same decision index.html makes again by hand for #shell-seo. */
export function firstTab(search: string): SeoTab {
  let asked: string | null = null;
  try {
    asked = new URLSearchParams(search).get("tab");
  } catch {
    /* a malformed query is no tab */
  }
  if (isSeoTab(asked)) return asked;
  const kept = recall(LAST_SEO_TAB);
  return isSeoTab(kept) ? kept : "overview";
}

/** Search's own three parts, shown one at a time inside the tab (Google,
 *  Rankings, Bing), because the three in a row were most of the old page's
 *  length on their own. */
export type SearchPart = "google" | "rankings" | "bing";

export const SEARCH_PARTS: { key: SearchPart; label: string }[] = [
  { key: "google", label: "Google" },
  { key: "rankings", label: "Rankings" },
  { key: "bing", label: "Bing" },
];

export function isSearchPart(value: unknown): value is SearchPart {
  return SEARCH_PARTS.some((p) => p.key === value);
}

/** A way into a tab from another: the tab, and the section in it to land on
 *  (a section's id without its "seo-sec-" prefix, or one of Search's parts).
 *  Without a section, the tab opens where it was left. */
export type GoTo = (tab: SeoTab, section?: string) => void;
